import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import type { AppDispatch } from '../../../shared/store'
import { processSync } from '../../../shared/store/slices/syncSlice'

/** How often to re-attempt draining pending queue items while online */
export const SYNC_DRAIN_INTERVAL_MS = 60_000

/**
 * Drains the sync queue on app start, on a steady interval, and when the
 * page returns to the foreground.
 *
 * The window 'online' event (handled in useSyncStatus) is NOT a sufficient
 * trigger on its own: saves that fell back to the queue over a dead link
 * (navigator.onLine never flipped, so the event never fires) and queues
 * restored into a freshly loaded tab (the reconnect happened before the
 * listener existed) would otherwise sit pending forever.
 *
 * The visibilitychange trigger matters most on iOS: timers don't tick
 * while the app is backgrounded, and returning to the foreground is
 * exactly when field users regain a connection.
 *
 * Mount exactly once (App). processSync no-ops cheaply when the queue is
 * empty and is guarded against overlapping runs in the thunk itself.
 */
export const useSyncQueueDrain = () => {
  const dispatch = useDispatch<AppDispatch>()

  useEffect(() => {
    const drain = () => {
      if (!navigator.onLine) return
      void dispatch(processSync())
    }

    const drainOnForeground = () => {
      if (document.visibilityState === 'visible') drain()
    }

    drain()
    const interval = setInterval(drain, SYNC_DRAIN_INTERVAL_MS)
    document.addEventListener('visibilitychange', drainOnForeground)
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', drainOnForeground)
    }
  }, [dispatch])
}
